import { useMemo } from 'react';
import { Chart } from '@tanstack/react-charts';
import { defineChart } from '@tanstack/charts';
import { pie, polar, radialArc } from '@tanstack/charts/polar';
import { ReactNode } from 'react';
import Dot from '@/components/ui/Dot.tsx';
import { cn } from '@/lib/utils.ts';

export interface DonutChartSlice {
  id: string;
  label: string;
  value: number;
  color: string;
  /** Kwota albo liczba w legendzie — już sformatowana. */
  formattedValue?: ReactNode;
}

interface DonutChartProps extends React.HTMLAttributes<HTMLDivElement> {
  slices: DonutChartSlice[];
  height?: number;
  ariaLabel: string;
}

/**
 * Pierścień z legendą — udziały kategorii kosztów. Jedyne miejsce, które zna
 * `@tanstack/charts/polar`.
 *
 * Legenda stoi obok, a nie pod spodem: kategorii jest kilka, a każdą czyta się
 * razem z kwotą, więc pionowa lista czyta się lepiej niż rząd pod wykresem.
 */
const DonutChart = ({
  slices,
  height = 200,
  ariaLabel,
  className,
  ...props
}: DonutChartProps) => {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  const definition = useMemo(
    () =>
      defineChart({
        marks: [
          polar({
            inset: 4,
            radiusRatio: 0.9,
            marks: [
              radialArc(pie(slices, { value: 'value' }), {
                innerRadius: ({ radius }) => radius * 0.58,
                cornerRadius: 2,
                color: 'id',
                key: 'id',
              }),
            ],
          }),
        ],
        color: {
          domain: slices.map((slice) => slice.id),
          range: slices.map((slice) => slice.color),
        },
        guides: false,
        margin: 0,
      }),
    [slices],
  );

  return (
    <div
      className={cn('flex flex-wrap items-center gap-6', className)}
      {...props}
    >
      <div className="shrink-0" style={{ width: height, height }}>
        <Chart definition={definition} height={height} ariaLabel={ariaLabel} />
      </div>

      <div className="flex min-w-[180px] flex-1 flex-col gap-2">
        {slices.map((slice) => (
          <div key={slice.id} className="flex items-baseline gap-2.5">
            <Dot
              size="md"
              className="shrink-0"
              style={{ backgroundColor: slice.color }}
            />
            <span className="text-sm text-neutral-700">{slice.label}</span>
            {total > 0 && (
              <span className="text-[13px] tabular-nums text-neutral-400">
                {Math.round((slice.value / total) * 100)}%
              </span>
            )}
            {slice.formattedValue && (
              <span className="ml-auto whitespace-nowrap text-[15px] font-bold tabular-nums text-ink">
                {slice.formattedValue}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DonutChart;
